import { useEffect, useRef, useState } from "react";
import { Button, Stack, TextField, Typography } from "@mui/material";
import { useTranslation } from "i18n-fe";

// #3568 -- the lock screen's PIN entry. A plain input (number keyboard), no
// on-screen PIN pad. It submits by itself when the entered length reaches the
// PIN's length: a wrong PIN flashes an error and clears, the right one fires
// onUnlock. Cancel is rendered ONLY when the caller passes onCancel (the gear's
// dialog does; the lock screen does not, #3635: no way past it without the PIN). The PIN itself comes from
// util/scribePin.js (the show's scribeUnlockPin, falling back to "5432").
// #3634: an optional label override, used by the gear's admin-PIN dialog. The
// lock screen passes none and keeps its own wording.
export default function ScribeLockPin({ pin, onUnlock, onCancel, label }) {
  const { t } = useTranslation("score");
  const [entered, setEntered] = useState("");
  const [error, setError] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (entered.length !== pin.length) return undefined;
    let clearTimer;
    const id = setTimeout(() => {
      if (entered === pin) {
        setEntered("");
        onUnlock?.();
      } else {
        setError(true);
        clearTimer = setTimeout(() => {
          setEntered("");
          setError(false);
          inputRef.current?.focus();
        }, 600);
      }
    }, 80);
    return () => {
      clearTimeout(id);
      clearTimeout(clearTimer);
    };
  }, [entered, pin, onUnlock]);

  return (
    <Stack spacing={2} alignItems="center" data-testid="scribe-lock-screen">
      <Typography variant="h6" component="label" htmlFor="scribe-pin-input" fontWeight={500}>
        {label ?? t("lock.pinLabel", { count: pin.length })}
      </Typography>
      <TextField
        autoFocus
        autoComplete="off"
        type="password"
        value={entered}
        inputRef={inputRef}
        onChange={(e) => {
          // Digits only, capped at the PIN's length so a paste cannot overflow the auto-submit.
          setEntered(e.target.value.replace(/\D/g, "").slice(0, pin.length));
          if (error) setError(false);
        }}
        error={error}
        helperText={error ? t("lock.wrongPin") : " "}
        slotProps={{
          htmlInput: {
            id: "scribe-pin-input",
            "data-testid": "scribe-pin-input",
            "aria-label": t("lock.pinInputAria"),
            inputMode: "numeric",
            pattern: "[0-9]*",
            maxLength: pin.length,
            style: {
              textAlign: "center",
              fontFamily: "monospace",
              fontSize: 32,
              letterSpacing: "0.6em",
              paddingLeft: "0.6em",
            },
          },
        }}
        sx={{ width: "100%", maxWidth: 420 }}
      />
      {onCancel && (
        <Button variant="outlined" color="inherit" onClick={onCancel} data-testid="scribe-lock-cancel">
          {t("lock.cancel")}
        </Button>
      )}
    </Stack>
  );
}
